/** Formatos y agrupaciones del módulo «Uso de la app». */
import type { DiaUsoApp } from '../../../services/usoAppService';

export type Granularidad = 'dia' | 'semana' | 'mes';

export const nf = new Intl.NumberFormat('es-PA');

/* Colores de serie: viven en variables del módulo (claro y oscuro validados en el CSS). */
export const SERIE_ANDROID = 'var(--serie-android)';
export const SERIE_IOS = 'var(--serie-ios)';
export const SERIE_UNICA = 'var(--serie-unica)';

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** «2026-10-19» → «19 oct 2026». */
export function fechaCorta(fecha: string): string {
  return `${Number(fecha.slice(8, 10))} ${MESES[Number(fecha.slice(5, 7)) - 1]} ${fecha.slice(0, 4)}`;
}

/** Lunes (yyyy-mm-dd) de la semana de una fecha. */
function lunesDe(fecha: string): string {
  const d = new Date(`${fecha}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}

function claveYEtiqueta(fecha: string, g: Granularidad): [string, string] {
  if (g === 'dia') return [fecha, `${fecha.slice(8)}/${fecha.slice(5, 7)}`];
  if (g === 'semana') { const l = lunesDe(fecha); return [l, `Sem. ${l.slice(8)}/${l.slice(5, 7)}`]; }
  return [fecha.slice(0, 7), `${MESES[Number(fecha.slice(5, 7)) - 1]} ${fecha.slice(0, 4)}`];
}

/**
 * Suma los valores de cada día en cubetas de día, semana (lunes a domingo) o mes.
 * Sirve para conteos (inicios de sesión, aperturas, cuentas); NO para usuarios únicos,
 * que no se pueden sumar entre días y vienen ya contados por período desde el servidor.
 */
export function agrupar<K extends string>(
  serie: DiaUsoApp[], g: Granularidad, valores: (d: DiaUsoApp) => Record<K, number>,
): Array<{ etiqueta: string } & Record<K, number>> {
  const cubetas = new Map<string, { etiqueta: string } & Record<string, number>>();
  for (const d of serie) {
    const [clave, etiqueta] = claveYEtiqueta(d.fecha, g);
    const c = cubetas.get(clave) ?? ({ etiqueta } as { etiqueta: string } & Record<string, number>);
    for (const [k, n] of Object.entries(valores(d)) as [string, number][]) c[k] = (c[k] ?? 0) + n;
    cubetas.set(clave, c);
  }
  return [...cubetas.values()] as Array<{ etiqueta: string } & Record<K, number>>;
}

/** Nombres legibles de las rutas más comunes de la app; el resto se separa por mayúsculas. */
const PANTALLAS: Record<string, string> = {
  Home: 'Inicio',
  Login: 'Inicio de sesión',
  LoginWithCode: 'Ingreso con código',
  SignUp: 'Registro',
  emailVerification: 'Verificación de correo',
  OnBoarding: 'Bienvenida',
  Account: 'Mi perfil',
  More: 'Más',
  Notification: 'Notificaciones',
  MiSalud: 'Mi salud',
  MiSaludMenu: 'Mi salud (menú)',
  Laboratorios: 'Laboratorios',
  ExamenesLaboratorio: 'Exámenes de laboratorio',
  ExamenesLaboratorioDetalle: 'Detalle de examen',
  Vacunas: 'Vacunas',
  Medicamentos: 'Medicamentos',
  Diagnosticos: 'Diagnósticos',
  Alergias: 'Alergias',
  SignosVitales: 'Signos vitales',
  ImagenesMedicas: 'Imágenes médicas',
  Consult: 'Citas',
  ConsultMenu: 'Citas (menú)',
  SelectSpecial: 'Elegir especialidad',
  SelectClinic: 'Elegir instalación',
  GetPrecita: 'Solicitar cita',
  GetPrecitaConfirm: 'Confirmar cupo',
  GetPrecitaSuccessConfirm: 'Cita confirmada',
  ConsultNextAppointment: 'Próximas citas',
  ConsultNextAppointmentSeis: 'Próximas citas (SEIS)',
  PastConsult: 'Citas anteriores',
  TeleconsultaSalas: 'Teleconsulta: salas',
  TeleconsultaDisclaimer: 'Teleconsulta: aviso',
  TeleconsultaDeclaracion: 'Teleconsulta: declaración',
  TeleconsultaTriage: 'Teleconsulta: triage',
  TeleconsultaTriageVoz: 'Teleconsulta: triage por voz',
  TeleconsultaEspera: 'Teleconsulta: sala de espera',
  TeleconsultaLlamada: 'Teleconsulta: llamada entrante',
  TeleconsultaVideo: 'Teleconsulta: videollamada',
  TeleconsultaFin: 'Teleconsulta: fin',
  TeleconsultaEmergencia: 'Teleconsulta: derivación',
  MisTeleconsultas: 'Mis teleconsultas',
  KYCFlow: 'Registro biométrico',
  KYCResult: 'Resultado biométrico',
  CampaignsList: 'Campañas',
  CampaignDetail: 'Detalle de campaña',
  CompletarPerfil: 'Completar perfil',
};

export function nombrePantalla(ruta: string): string {
  return PANTALLAS[ruta] ?? ruta.replace(/([a-z])([A-Z])/g, '$1 $2');
}

/** Acciones agrupadas por área (los tipos son los del backend). */
export const AREAS: Array<{ area: string; eventos: string[] }> = [
  { area: 'Cuenta', eventos: ['registro_cuenta', 'login'] },
  { area: 'Teleconsulta', eventos: ['teleconsulta_triage_completado', 'teleconsulta_solicitada', 'teleconsulta_llamada_iniciada', 'teleconsulta_finalizada'] },
  { area: 'Citas', eventos: ['cita_solicitud_iniciada', 'cita_creada', 'cita_cancelada', 'cita_cancelada_paciente'] },
  { area: 'Registro biométrico', eventos: ['kyc_iniciado', 'kyc_aprobado', 'kyc_revision_manual', 'kyc_rechazado'] },
  { area: 'Perfil', eventos: ['perfil_actualizado', 'foto_perfil_actualizada'] },
  { area: 'Notificaciones', eventos: ['notificacion_abierta'] },
];
